import React, { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Upload, Clock, Video } from "lucide-react";

export default function Verification() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const videoRef = useRef(null);

  const submit = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/verification/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Selfie submitted for verification");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profiles/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video intro uploaded");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const status = user?.verification_status;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 space-y-6">
      <Card className="p-8 border-[#EAE3D2] bg-white">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-10 h-10 text-[#6B1D2F]" />
          <div>
            <h1 className="font-serif text-3xl text-slate-900">Verified Profile Badge</h1>
            <p className="text-slate-600 mt-1">Upload a clear selfie so our team can add a Verified badge to your profile.</p>
          </div>
        </div>

        <div className="mt-6">
          {status === "verified" && <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 px-4 py-2"><ShieldCheck className="w-4 h-4 mr-1" /> You are Verified</Badge>}
          {status === "pending" && <Badge className="bg-amber-50 text-amber-800 border-amber-200 px-4 py-2"><Clock className="w-4 h-4 mr-1" /> Under review</Badge>}
          {status === "rejected" && <Badge className="bg-red-50 text-red-800 border-red-200 px-4 py-2">Not approved — please resubmit</Badge>}
          {!status && <Badge className="bg-slate-100 text-slate-800 px-4 py-2">Not verified yet</Badge>}
        </div>

        <input type="file" ref={ref} accept="image/*" hidden data-testid="verification-file-input" onChange={(e) => submit(e.target.files?.[0])} />
        <Button onClick={() => ref.current?.click()} disabled={busy || status === "pending"} data-testid="verification-submit-btn" className="mt-6 bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full">
          <Upload className="w-4 h-4 mr-2" /> {busy ? "Uploading…" : status === "pending" ? "Awaiting review" : "Upload selfie"}
        </Button>
      </Card>

      {status === "verified" ? (
        <Card className="p-8 border-[#EAE3D2] bg-white">
          <div className="flex items-center gap-3">
            <Video className="w-10 h-10 text-[#6B1D2F]" />
            <div>
              <h2 className="font-serif text-2xl text-slate-900">30-second Video Intro</h2>
              <p className="text-slate-600 mt-1">Record a short intro on your phone (up to 30s, 25MB) to boost your response rate.</p>
            </div>
          </div>
          {user?.video_intro_path && (
            <video controls src={fileUrl(user.video_intro_path)} className="mt-4 w-full rounded-lg bg-black" data-testid="video-preview" />
          )}
          <input type="file" ref={videoRef} accept="video/*" hidden data-testid="video-file-input" onChange={(e) => uploadVideo(e.target.files?.[0])} />
          <Button onClick={() => videoRef.current?.click()} disabled={busy} data-testid="video-upload-btn" className="mt-6 bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full">
            <Upload className="w-4 h-4 mr-2" /> {user?.video_intro_path ? "Replace video" : "Upload video intro"}
          </Button>
        </Card>
      ) : (
        <Card className="p-6 border-[#EAE3D2] bg-[#FAF6F0]">
          <div className="flex items-center gap-3 text-slate-600">
            <Video className="w-6 h-6" />
            <p className="text-sm">Video intros unlock once your profile is <b>Verified</b>.</p>
          </div>
        </Card>
      )}
    </div>
  );
}

